import { flexRender, PaginationState, Table } from '@tanstack/react-table';
import {
  TableBody,
  Skeleton,
  TableRow,
  TableCell,
  cn,
  getRowStyling,
} from '@sib/ui';
import { useIsLoadingDelayed } from '@sib/ui/hooks';
import { getTableColumnPinningProperties } from '../../utils';
import {
  SibTableDelayedLoadingIndicator,
  SibTableDelayedLoadingIndicatorProps,
} from './SibTableDelayedLoadingIndicator';
import {
  borderLeftPinnedColumnBg,
  borderRightPinnedColumnBg,
  pinnedColumnBg,
  pinnedLeftShadow,
  pinnedRightShadow,
} from './SibTableBody.style';

interface SibTableBodyProps<T> extends Pick<PaginationState, 'pageSize'> {
  table: Table<T>;
  loading?: boolean;
  withDelayedLoading?: boolean;
  isEmpty?: boolean;
  emptyPlaceholder?: React.ReactNode;
  TableDelayedLoadingIndicatorProps: SibTableDelayedLoadingIndicatorProps;
}

export const SibTableBody = <T,>({
  table,
  withDelayedLoading,
  emptyPlaceholder,
  isEmpty,
  pageSize,
  loading = false,
  TableDelayedLoadingIndicatorProps,
}: SibTableBodyProps<T>) => {
  const isLoadingDelayed = useIsLoadingDelayed({
    loading,
  });

  const columnsCount = table.getVisibleLeafColumns().length;

  return (
    <TableBody className="relative">
      {loading ? (
        <>
          {withDelayedLoading && (
            <SibTableDelayedLoadingIndicator
              {...TableDelayedLoadingIndicatorProps}
              visible={isLoadingDelayed}
              isEmpty={isEmpty}
            />
          )}

          {Array.from({
            length: pageSize,
          }).map((_, index) => (
            <TableRow key={`table-row-skeleton-${index}`}>
              <TableCell colSpan={columnsCount}>
                <Skeleton className="size-full rounded-sm bg-[#E8EFF6]" />
              </TableCell>
            </TableRow>
          ))}
        </>
      ) : !isEmpty ? (
        table.getRowModel().rows.map((row, index) => {
          const canRowExpand = row.getCanExpand();

          return (
            <TableRow
              key={row.id}
              {...(canRowExpand && {
                onClick: () => row.toggleExpanded(),
              })}
              className={cn(
                canRowExpand && 'cursor-pointer',
                getRowStyling({
                  row,
                  index,
                  rows: table.getRowModel().rows,
                })
              )}
            >
              {row.getVisibleCells().map(cell => {
                const cellContext = cell.getContext();
                const className = cellContext.column.columnDef.meta?.className;

                const aplicableClassName =
                  typeof className === 'string'
                    ? className
                    : className?.(row.original);

                const {
                  pinPosition,
                  isLeftPinned,
                  isRightPinned,
                  shouldApplyShadow,
                  style,
                } = getTableColumnPinningProperties(cell.column, table);

                return (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      aplicableClassName,
                      '[&>button]:w-full',
                      'transition-colors duration-100',
                      pinPosition && 'sticky z-10',
                      pinPosition && !shouldApplyShadow && pinnedColumnBg,
                      isLeftPinned &&
                        shouldApplyShadow &&
                        `${pinnedLeftShadow} ${borderLeftPinnedColumnBg}`,
                      isRightPinned &&
                        shouldApplyShadow &&
                        `${pinnedRightShadow} ${borderRightPinnedColumnBg}`
                    )}
                    style={style}
                  >
                    {flexRender(cell.column.columnDef.cell, cellContext)}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })
      ) : (
        emptyPlaceholder || null
      )}
    </TableBody>
  );
};
